import { Button, CircularProgress } from '@mui/material';
import { memo, useEffect, useState } from 'react';

import DoughnutChart from 'components/ui/charts/DoughnutChart';
import { useQuery } from 'react-query';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useSearch from 'hooks/useSearch';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import LeadInfoModal from './LeadInfoModal';
import ContractInfoModal from './ContractInfoModal';

function getRandomColor() {
  var letters = '0123456789ABCDEF';
  var color = '#';
  for (var i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

export default memo(function Widget({ info }) {
  const query = useSearch();
  const { search } = useLocation();
  const axiosPrivate = useAxiosPrivate();
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [values, setValues] = useState([]);
  const [colors, setColors] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [dataInfoModal, setDataInfoModal] = useState(false);

  const { data: widget, isLoading } = useQuery({
    queryKey: ['/admin/widgets/chart2', search, info?.id],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `/admin/widgets/chart2${search ? search + '&' : '?'}type=${
          info?.type
        }&id=${info?.id}`
      );
      return response.data.data;
    },
    enabled: !hasError && !!query.get('dashboard') && !!info,
    onSuccess: (data) => {},
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  useEffect(() => {
    if (!widget) return;

    if (info?.type != 'CONTRACTSTAFF' && info?.type != 'COMETOOFFICE') {
      const labels = Object?.keys(widget);
      const values = Object?.values(widget);
      const colors = Object?.values(widget)?.map(() => getRandomColor());
      setData(
        Object?.entries(widget)?.map(([name, count], i) => ({
          name,
          value: count,
          color: colors?.[i],
        }))
      );
      setTotal(values?.reduce((sum, d) => sum + d, 0));
      setLabels(labels);
      setValues(values);
      setColors(colors);
    } else if (info?.type == 'COMETOOFFICE') {
      const labels = Object.keys(widget?.counts);
      const values = Object?.values(widget?.counts);
      const events = labels?.map((label) => widget?.events?.[label] || []);

      const colors = labels?.map(() => getRandomColor());
      setData(
        labels?.map((item, i) => ({
          name: item,
          value: values?.[i],
          events: events?.[i],
          color: colors?.[i],
        }))
      );
      setTotal(values?.reduce((sum, d) => sum + d, 0));
      setLabels(labels);
      setValues(values);
      setColors(colors);
    } else if (info?.type == 'CONTRACTSTAFF') {
      const labels = Object.keys(widget?.result);
      const values = Object?.values(widget?.result);
      const contracts = Object?.values(widget?.contracts)?.reduce(
        (acc, cur) => {
          return { ...acc, [cur.name]: cur?.contracts };
        },
        {}
      );

      const events = labels?.map((label) => contracts?.[label] || []);

      const colors = labels?.map(() => getRandomColor());
      setData(
        labels?.map((item, i) => ({
          name: item,
          value: values?.[i],
          contracts: events?.[i],
          color: colors?.[i],
        }))
      );
      setTotal(values?.reduce((sum, d) => sum + d, 0));
      setLabels(labels);
      setValues(values);
      setColors(colors);
    }
  }, [widget]);
  const onClick = (item) => {
    if (info?.type == 'COMETOOFFICE' || info?.type == 'CONTRACTSTAFF') {
      setOpenInfoModal(true);
      setDataInfoModal(item);
    }
  };
  return (
    <div className='bg-white mb-4 rounded-lg'>
      <div className=''>
        <div className='flex items-center justify-center rounded-t-lg py-4 bg-gray-50 mb-2'>
          <h2>{info?.name}</h2>
        </div>
        {isLoading ? (
          <div className='h-[260px] flex items-center justify-center'>
            <CircularProgress size={30} color='inherit' />
          </div>
        ) : (
          <div
            className={'flex gap-4 flex-col items-center py-[24px] px-[32px]'}
          >
            <div className='relative h-96 w-96 min-w-96'>
              <DoughnutChart
                chartLabels={labels}
                chartDatasets={[
                  {
                    data: values,
                    backgroundColor: colors,
                    borderWidth: 2,
                    hoverOffset: 15,
                    hoverBorderWidth: 0,
                    borderRadius: 4,
                  },
                ]}
                suffix=''
                optionPlugins={{
                  legend: {
                    display: false,
                  },
                }}
                options={{
                  layout: {
                    padding: {
                      top: 10,
                      bottom: 10,
                    },
                  },
                }}
              />
              {/* {totalTask > 0 && ( */}
              <div className='text-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
                <h1 className='text-[35px]'>{total.toLocaleString()}</h1>
                <p className='text-sm text-gray-500'>
                  {t('common.table.total')}
                </p>
              </div>
              {/* )} */}
            </div>
            {/* <div className='max-h-[50vh] my-scroll overflow-y-auto'> */}
            <div className='text-sm overflow-x-auto my-scroll w-full max-h-[500px]'>
              <Button
                fullWidth
                className='!justify-between !text-gray-700 !bg-gray-100'
              ></Button>

              {data?.map((item, i) => (
                <div
                  key={`${i}hodimlar`}
                  className='flex justify-between text-[16px] font-medium text-[#464255] w-full py-3 px-2 my-1.5 cursor-pointer hover:bg-gray-50 rounded-lg'
                  onClick={() => {
                    onClick(item);
                  }}
                >
                  <div className='flex items-center whitespace-nowrap'>
                    <div
                      className={`min-w-[18px] mr-1 h-[18px] border-4 rounded-full`}
                      style={{ borderColor: item?.color }}
                    />
                    {item?.name}
                  </div>
                  <div className='flex items-center whitespace-nowrap'>
                    {item?.value.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {info?.type == 'COMETOOFFICE' && (
        <LeadInfoModal
          open={openInfoModal}
          setOpen={setOpenInfoModal}
          data={dataInfoModal}
          setData={setDataInfoModal}
        />
      )}
      {info?.type == 'CONTRACTSTAFF' && (
        <ContractInfoModal
          open={openInfoModal}
          setOpen={setOpenInfoModal}
          data={dataInfoModal}
          setData={setDataInfoModal}
        />
      )}
    </div>
  );
});
